import { Glue42Core } from "@glue42/core";

import {CallbackRegistry} from "callback-registry";

import {AnonymousFunction} from "./types";
import {
    OnSecureReplyCallback,
    OnDisplaySecureEmailCallback,
    OnAddinStatusChangedCallback,
    TrackCalendarEventCallback,
    TrackEmailCallback,
    UntrackCalendarEventCallback,
    UntrackEmailCallback,
    OnEmailReceivedCallback,
    OnTaskCreatedCallback,
} from "./types";
import {isOutlookEnabled} from "./helpers/index";
import {
    registerCRMTrackEmail, registerCRMTrackItem, registerCRMUntrackEmail, registerCRMUntrackItem,
} from "./registerAgmMethods";
import {
    CreateItemMethodName,
    OnTrackEmailMethodName,
    OnTrackItemMethodName,
    OnUntrackEmailMethodName,
    OnUntrackItemMethodName,
} from "./helpers/methodNames";

export let connected: boolean = false;

const onOutlookEnabled = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    if (isOutlookEnabled(agm)) {
        connected = true;
    }
    agm.methodAdded((m: {name: string}) => {
        if (m.name === CreateItemMethodName) {
            if (connected === false) {
                connected = true;
                callbacks.execute("onAddinStatusChanged", {connected});
            }
        }
    });
};

const onOutlookDisabled = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    agm.methodRemoved((m: {name: string}) => {
        if (m.name === CreateItemMethodName) {
            connected = false;
            callbacks.execute("onAddinStatusChanged", {connected});
        }
    });
};

export const registerEventListeners = (agm: Glue42Core.AGM.API, callbacks: CallbackRegistry) => {
    onOutlookEnabled(agm, callbacks);
    onOutlookDisabled(agm, callbacks);
};

export const registerOnAddinStatusChanged = (agm: Glue42Core.AGM.API,
                                             callback: OnAddinStatusChangedCallback,
                                             callbacks: CallbackRegistry): AnonymousFunction => {
    callback({connected});
    return callbacks.add("onAddinStatusChanged", callback);
};

export const registerOnEmailReceived = (agm: Glue42Core.AGM.API,
                                        callback: OnEmailReceivedCallback,
                                        callbacks: CallbackRegistry): AnonymousFunction => {
    return callbacks.add("onEmailReceived", callback);
};

export const registerOnTaskCreated = (agm: Glue42Core.AGM.API,
                                      callback: OnTaskCreatedCallback,
                                      callbacks: CallbackRegistry): AnonymousFunction => {
    return callbacks.add("onTaskCreated", callback);
};

const unsubscribeFunction = (uns: () => void, agm: Glue42Core.AGM.API, methodName: string) => {
    uns();
    if (agm.methods({name: methodName}).length > 0) {
        agm.unregister(methodName);
    }
};

export const registerTrackEmail = (agm: Glue42Core.AGM.API,
                                   callback: TrackEmailCallback,
                                   callbacks: CallbackRegistry): AnonymousFunction => {
    if (agm.methods({name: OnTrackEmailMethodName}).length === 0) {
        registerCRMTrackEmail(agm, callbacks);
        const uns = callbacks.add("onEmailTracked", callback);
        return () => unsubscribeFunction(uns, agm, OnTrackEmailMethodName);
    } else {
        throw Error("Another client has already subscribed for tracking emails");
    }
};

export const registerUntrackEmail = (agm: Glue42Core.AGM.API,
                                     callback: UntrackEmailCallback,
                                     callbacks: CallbackRegistry): AnonymousFunction => {
    if (agm.methods({name: OnUntrackEmailMethodName}).length === 0) {
        registerCRMUntrackEmail(agm, callbacks);
        const uns = callbacks.add("onEmailUntracked", callback);
        return () => unsubscribeFunction(uns, agm, OnUntrackEmailMethodName);
    } else {
        throw Error("Another client has already subscribed for untracking emails");
    }
};

export const registerTrackItem = (agm: Glue42Core.AGM.API,
                                  callback: TrackCalendarEventCallback,
                                  callbacks: CallbackRegistry): AnonymousFunction => {
    if (agm.methods({name: OnTrackItemMethodName}).length === 0) {
        registerCRMTrackItem(agm, callbacks);
        const uns = callbacks.add("onItemTracked", callback);
        return () => unsubscribeFunction(uns, agm, OnTrackItemMethodName);
    } else {
        throw Error("Another client has already subscribed for tracking calendar events");
    }
};

export const registerUntrackItem = (agm: Glue42Core.AGM.API,
                                    callback: UntrackCalendarEventCallback,
                                    callbacks: CallbackRegistry): AnonymousFunction => {
    if (agm.methods({name: OnUntrackItemMethodName}).length === 0) {
        registerCRMUntrackItem(agm, callbacks);
        const uns = callbacks.add("onItemUntracked", callback);
        return () => unsubscribeFunction(uns, agm, OnUntrackItemMethodName);
    } else {
        throw Error("Another client has already subscribed for untracking calendar events");
    }
};

export const registerSecureReply = (agm: Glue42Core.AGM.API,
                                    callback: OnSecureReplyCallback,
                                    callbacks: CallbackRegistry): AnonymousFunction => {
    return callbacks.add("onSecureReply", callback);
};

export const registerDisplaySecureEmail = (agm: Glue42Core.AGM.API,
                                           callback: OnDisplaySecureEmailCallback,
                                           callbacks: CallbackRegistry): AnonymousFunction => {
    return callbacks.add("onDisplaySecureEmail", callback);
};
